import { Col, Form, Row } from "antd";
import type { Rule } from "antd/lib/form";
import i18n from "i18next";
import { type FC, useCallback } from "react";

import type { DynamicPropertyValue } from "../../../../core/types/fe/entities/dynamicPropertyEntity";
import { getValidationRules } from "../../components/Validations/ValidationFieldsFactory";
import { FormFieldLabelStyle } from "../../formFieldLabelPositioning/styles/FormFieldsStyle";
import { withLabelColon } from "../../formFieldLabelPositioning/withLabelColon";
import type { ValidationRule } from "../../types/types";
import { ValidationType } from "../../types/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { TextInput } from "../Text/TextInput";
import { Textarea } from "../Textarea/Textarea";

interface DynamicPropertiesRenderProps {
    value?: DynamicPropertyValue[];
    useReadOnly?: boolean;
}

export const DynamicPropertiesRender: FC<DynamicPropertiesRenderProps> = ({ value, useReadOnly = true }) => {
    const createValidationRules = useCallback((required: boolean, pattern?: string, isJson?: boolean): Rule[] => {
        const rules = [] as ValidationRule[];
        if (required) {
            rules.push({ name: ValidationType.VALIDATION_REQUIRED, params: [] });
        }
        if (pattern && pattern !== "") {
            rules.push({ name: ValidationType.PATTERN, params: [pattern] });
        }

        const validationRules = getValidationRules(rules);

        if (isJson) {
            validationRules.push({
                validator: async (_, input) => {
                    if (!input || input.trim() === "") return Promise.resolve();
                    try {
                        JSON.parse(input);
                        return await Promise.resolve();
                    } catch (err) {
                        return Promise.reject(new Error("Invalid JSON format"));
                    }
                },
            });
        }

        return validationRules;
    }, []);

    return (
        <div>
            {value
                ?.map((prop, index) => ({ prop, index }))
                .filter(({ prop }) => prop.id !== undefined)
                .sort((a, b) => i18n.t(a.prop.label ?? "").localeCompare(i18n.t(b.prop.label ?? "")))
                .map(({ prop, index }) => (
                    <Row key={`${prop.key}FormItemRow`} gutter={20}>
                        <Col span={18} key={`${prop.key}FormItemCol`}>
                            {(() => {
                                switch (prop.type) {
                                    case "TEXT":
                                        return (
                                            <>
                                                <FormFieldLabelStyle>{withLabelColon(i18n.t(prop.label ?? ""))}</FormFieldLabelStyle>
                                                <Form.Item
                                                    key={`${prop.key}FormItemValue`}
                                                    name={["dynamicProperties", "dynamicPropertyValues", index, "value"]}
                                                    rules={createValidationRules(prop.mandatory || false, prop.pattern)}
                                                >
                                                    <TextInput key={prop.key} name={prop.key} readOnly={prop.readOnly || false} />
                                                </Form.Item>
                                            </>
                                        );
                                    case "JSON":
                                        return (
                                            <>
                                                <FormFieldLabelStyle>{withLabelColon(i18n.t(prop.label ?? ""))}</FormFieldLabelStyle>
                                                <Form.Item
                                                    key={`${prop.key}FormItemValue`}
                                                    name={["dynamicProperties", "dynamicPropertyValues", index, "value"]}
                                                    rules={createValidationRules(prop.mandatory || false, prop.pattern, true)}
                                                >
                                                    <Textarea key={prop.key} name={prop.key} readOnly={prop.readOnly || false} />
                                                </Form.Item>
                                            </>
                                        );
                                    default:
                                        return (
                                            <>
                                                <FormFieldLabelStyle>{withLabelColon(i18n.t(prop.label ?? ""))}</FormFieldLabelStyle>
                                                <Form.Item
                                                    key={`${prop.key}FormItemValue`}
                                                    name={["dynamicProperties", "dynamicPropertyValues", index, "value"]}
                                                    rules={createValidationRules(prop.mandatory || false, prop.pattern)}
                                                    valuePropName="checked"
                                                    getValueFromEvent={(e) => e === true || e === "true"}
                                                    getValueProps={(val) => ({ checked: val === true || val === "true" })}
                                                >
                                                    <Checkbox name={prop.key!} label="" readOnly={prop.readOnly || false} />
                                                </Form.Item>
                                            </>
                                        );
                                }
                            })()}
                        </Col>
                        <Col key={`${prop.key}FormItemColRO`} span={6}>
                            {useReadOnly && (
                                <>
                                    <FormFieldLabelStyle>{withLabelColon(i18n.t("form.readOnly"))}</FormFieldLabelStyle>
                                    <Form.Item
                                        key={`${prop.key}FormItemRO`}
                                        name={["dynamicProperties", "dynamicPropertyValues", index, "readOnly"]}
                                        rules={[]}
                                    >
                                        <Checkbox name={`${prop.key}RO`} label="" readOnly={false} />
                                    </Form.Item>
                                </>
                            )}
                        </Col>
                        {/* <Col span={0} key={`${prop.key}-ColHidden`}> */}
                        {/*    <Form.Item key={`${prop.key}-key`} name={["dynamicProperties", "dynamicPropertyValues", index, "key"]} hidden> */}
                        {/*        <input type="hidden" value={prop.key} /> */}
                        {/*    </Form.Item> */}
                        {/*    <Form.Item key={`${prop.id}-Id`} name={["dynamicProperties", "dynamicPropertyValues", index, "id"]} hidden> */}
                        {/*        <input type="hidden" value={prop.id} /> */}
                        {/*    </Form.Item> */}
                        {/*    <Form.Item */}
                        {/*        key={`${prop.key}-Label`} */}
                        {/*        name={["dynamicProperties", "dynamicPropertyValues", index, "label"]} */}
                        {/*        hidden */}
                        {/*    > */}
                        {/*        <input type="hidden" value={prop.label} /> */}
                        {/*    </Form.Item> */}
                        {/*    <Form.Item */}
                        {/*        key={`${prop.key}-Pattern`} */}
                        {/*        name={["dynamicProperties", "dynamicPropertyValues", index, "pattern"]} */}
                        {/*        hidden */}
                        {/*    > */}
                        {/*        <input type="text" value={prop.pattern || ""} hidden /> */}
                        {/*    </Form.Item> */}
                        {/* </Col> */}
                    </Row>
                    // </>
                ))}
        </div>
    );
};
